// oxlint-disable typescript/no-explicit-any
import { action, observable, runInAction, makeObservable } from 'mobx';
import Pusher, { Channel } from 'pusher-js';
import { eventBus, subscribe } from 'mobx-event-bus2';
import mixpanel from 'mixpanel-browser';

import { ChannelAuthorizationData } from 'pusher-js/types/src/core/auth/options';

import { AxiosResponse } from 'axios';

import Clarity from '@microsoft/clarity';

import { initHubSpot, resetHubSpot } from 'utils/hubspot';

import { authenticateKnockClient, resetKnockClient } from 'utils/knock/knockClient';

import { MixpanelEvent, MixpanelEventProperties } from 'utils/analytics/events';

import routes from 'core/routes';

import Logger from 'utils/logger';

import { env } from 'config';

import { UserProfileResponse } from 'utils/api/modules/user/user.types';
import { BackgroundOption } from 'utils/api/types';
import { startActivityListener, stopActivityListener } from 'utils/api/activityPing';

import { timezoneStore } from 'stores/Timezone';

import { EventType } from '../../utils/events/constants';
import { postMessage, channel as broadcastChannel } from '../../utils/events/broadcast';
import { ActionEvent, BroadcastMessage, DeletedUsersPayload } from '../../utils/events/types';
import RootStore from '../Root';
import API from '../../utils/api';
import SessionUser from './SessionUser';
import { PusherConnectionState } from './types';
import { BackendEvent } from './BackendEvent';

export default class SessionStore {
  store: RootStore;

  api: typeof API;

  @observable user: SessionUser | null = null;

  @observable isAuthenticated = false;

  @observable hasWebsocketError = false;

  @observable hasOrganisationLogo = false;

  @observable hasHubSpot = false;

  socket?: any; // Type is Pusher.Pusher but the typecheck is showing errors. Maybe a mistmach with the types.

  websocketChannel?: any; // Type is Channel, but the subscribe is showing errors. Maybe a mitchmach with the types.

  pusherNotifications = new EventTarget();

  private tiptapTokenRefreshInterval?: ReturnType<typeof setInterval>;

  constructor(rootStore: RootStore, api: typeof API) {
    makeObservable(this);
    this.store = rootStore;
    this.api = api;

    broadcastChannel.onmessage = this.dispatchEvent;

    eventBus.register(this);
  }

  resubscribeWebsocketChannels = (): void => {
    const channels: Channel[] = this.socket?.allChannels() ?? [];
    channels.forEach((channel) => {
      if (!channel.subscribed && !channel.subscriptionPending) {
        channel.subscribe();
      }
    });
  };

  initWebsocket(): void {
    if (!this.user) {
      Logger.error('Unable to init websocket. Missing user');
      return;
    }
    if (!env.REACT_APP_PUSHER_KEY) {
      Logger.error('Unable to init websocket. Missing "REACT_APP_PUSHER_KEY" variable');
      return;
    }
    this.socket = new Pusher(env.REACT_APP_PUSHER_KEY, {
      cluster: env.REACT_APP_PUSHER_CLUSTER as string,
      authEndpoint: env.REACT_APP_PUSHER_AUTH_ENDPOINT,
      authorizer: (channel: Channel) => ({
        authorize: async (
          socketId: string,
          callback: (error: Error | null, authData: ChannelAuthorizationData) => void
        ) => {
          try {
            const response = await this.api.authWebsocket(socketId, channel.name, { background: true })();
            runInAction(() => {
              this.hasWebsocketError = false;
            });
            callback(null, response.data);
          } catch (e) {
            Logger.error(`Unable to authenticate websocket. Channel: ${channel}`, e);
            runInAction(() => {
              this.hasWebsocketError = true;
            });
            callback(e as Error, {} as ChannelAuthorizationData);
          }
        },
      }),
    });
    this.socket.connection.bind('error', (error: any): void => {
      let code = 'N/A';
      try {
        code = error.data.code;
      } catch (e) {
        Logger.warn('Unable to parse Websocket error code');
      }
      Logger.warn(`Websocket connection error. Error code: ${code}`, error);
    });

    this.socket.connection.bind(
      'state_change',
      (states: { previous: PusherConnectionState; current: PusherConnectionState }): void => {
        runInAction(() => {
          this.hasWebsocketError = states.current !== PusherConnectionState.CONNECTED;
        });
      }
    );

    this.websocketChannel = this.socket.subscribe(`private-${this.user.uuid}`);

    this.websocketChannel.bind('event', this.dispatchEvent);
  }

  @action.bound
  loadOrganisationLogo(): void {
    this.api.organisations
      .loadOrganisationLogo()()
      .then((response: AxiosResponse<Blob>) => {
        const objectURL = URL.createObjectURL(response.data);
        if (response.status === 204) {
          this.hasOrganisationLogo = false;
        } else {
          this.hasOrganisationLogo = true;
        }
        const imageElement = document.querySelector('#company-logo') as HTMLImageElement;
        if (imageElement) {
          imageElement.src = objectURL;
        }
      })
      .catch((error: Error) => {
        Logger.error('Invalid update user details API response', error);
      });
  }

  @action.bound
  login(data: UserProfileResponse): void {
    timezoneStore.startSession(data);
    this.user = new SessionUser(data);
    this.isAuthenticated = true;

    postMessage(EventType.LoggedIn, null);

    this.initWebsocket();

    // init clarity only on production
    if (env.ENV_NAME === 'uk-prod' || env.ENV_NAME === 'stage') {
      Clarity.init(env.REACT_APP_CLARITY_PROJECT_ID as string);
    }

    Logger.handler.setUser({
      id: data.uuid,
      email: data.email,
      username: data.name,
    });

    if (data.useHubspot) {
      this.hasHubSpot = true;
      initHubSpot({
        email: data.email,
      }).catch((error: Error) => {
        Logger.warn('Failed to initialize HubSpot during login', error);
        runInAction(() => {
          this.hasHubSpot = false;
        });
      });
    }

    if (env.REACT_APP_KNOCK_PUBLIC_API_KEY) {
      authenticateKnockClient().catch((e) => {
        Logger.warn('Failed to initialize Knock client during login', e);
      });
    }

    mixpanel.identify(data.uuid);
    this.trackMixpanelEvent(MixpanelEvent.UserLoggedIn, {
      group_id: data.organisation.uuid,
      group_name: data.organisation.name,
      user_id: data.uuid,
      user_email: data.email,
    });

    this.startTiptapTokenRefresh();
    startActivityListener();
  }

  startTiptapTokenRefresh(): void {
    this.tiptapTokenRefreshInterval = setInterval(
      () => {
        this.refreshTiptapTokens(undefined, { background: true });
      },
      15 * 60 * 1000
    );
  }

  @action.bound
  async refreshTiptapTokens(signal?: AbortSignal, options?: BackgroundOption): Promise<boolean> {
    if (!this.user) return false;
    try {
      const { data } = await this.api.user.getTiptapUserToken(signal, options)();
      runInAction(() => {
        this.user?.setTiptapTokens(data);
      });
      return true;
    } catch (e) {
      Logger.warn('Failed to refresh Tiptap tokens', e);
      return false;
    }
  }

  dispatchEvent = ({ type, payload }: BroadcastMessage): void => {
    eventBus.post(type, payload);
    this.pusherNotifications.dispatchEvent(new BackendEvent(type, payload));
  };

  subscribeToDocumentChannel(documentUuid: string): void {
    if (!this.socket) return;
    const channel = this.socket.subscribe(`private-document-${documentUuid}`);
    channel.bind('event', this.dispatchEvent);
  }

  unsubscribeFromDocumentChannel(documentUuid: string): void {
    if (!this.socket) return;
    this.socket.unsubscribe(`private-document-${documentUuid}`);
  }

  @action.bound
  logout(): void {
    mixpanel.reset();
    postMessage(EventType.LoggedOut, null);
  }

  @action.bound
  trackMixpanelEvent<E extends MixpanelEvent>(event: E, properties?: MixpanelEventProperties[E]): void {
    if (env.JEST_WORKER_ID === undefined) {
      mixpanel.track(event, properties);
    }
  }

  @subscribe(EventType.LoggedOut)
  @action
  reset(): void {
    this.isAuthenticated = false;
    this.store.routing.push(routes.login);

    stopActivityListener();

    if (this.tiptapTokenRefreshInterval) {
      clearInterval(this.tiptapTokenRefreshInterval);
      this.tiptapTokenRefreshInterval = undefined;
    }

    if (this.user) {
      this.socket && this.socket.disconnect();
      eventBus.unregister(this.user);
      this.user = null;
    }

    // Reset Knock state on logout
    resetKnockClient();
    this.store.knockPreferences.reset();

    timezoneStore.reset();
    this.store.timezoneSettings.reset();

    // Reset HubSpot state on logout
    if (this.hasHubSpot) {
      resetHubSpot();
      this.hasHubSpot = false;
    }

    Logger.handler.setUser(null);
  }

  @subscribe(EventType.DeletedUsers)
  @action
  deletedUsers({ payload }: ActionEvent<DeletedUsersPayload>): void {
    if (this.user && payload.users.includes(this.user.uuid)) {
      this.logout();
    }
  }

  get hasEventsLicense(): boolean {
    // if organisation doesn't have events license:
    if (!this.user?.organisation.hasEventsLicenses) {
      return false;
    }

    // if organisation has events license, show events license checkbox
    return true;
  }

  get hasRisksLicense(): boolean {
    return Boolean(this.user?.organisation.hasRisksLicenses);
  }

  updateDashboardName(dashboardId: string, name: string): void {
    // we need to update the dashboard name in the mobx store, because we use these values in sidebar
    if (this.user === null || this.user.dashboards === null) return;

    this.user.dashboards.analytics = Object.fromEntries(
      Object.entries(this.user.dashboards.analytics).map(([key, value]) => {
        value.map((v) => {
          if (v.uuid === dashboardId) {
            v.name = name;
          }
          return v;
        });
        return [key, value];
      })
    );
  }
}
